import { ApiError } from './types';

export function classifyHearing(value: number) {
  if (value <= 25) return 'no';
  if (value <= 40) return 'some';
  if (value <= 70) return 'significant';
  return 'severe';
}

export function classifyHearingText(value: number, language = 'en') {
  switch (true) {
    case value <= 25:
      return language === 'es' ? 'Sin Dificultad' : 'No Difficulty';
    case value <= 40:
      return language === 'es' ? 'Algo de Dificultad' : 'Some Difficulty';
    case value <= 70:
      return language === 'es'
        ? 'Dificultad Significativa'
        : 'Significant Difficulty';
    default:
      return language === 'es' ? 'Dificultad Severa' : 'Severe Difficulty';
  }
}

export interface TColorCodes {
  severe: '#D55F19';
  significant: '#FA9052';
  some: '#FFBB93';
  no: '#3EB1D3';
}

export const colorCodes: TColorCodes = {
  severe: '#D55F19',
  significant: '#FA9052',
  some: '#FFBB93',
  no: '#3EB1D3',
};

export async function hmacEncode(jsonStr: string, secret: string) {
  // Convert the JSON string and secret to Uint8Array using TextEncoder
  const encoder = new TextEncoder();
  const data = encoder.encode(jsonStr);
  const keyData = encoder.encode(secret);

  // Import the secret key for use with HMAC
  const key = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: { name: 'SHA-256' } },
    false,
    ['sign'],
  );

  // Sign the data with HMAC-SHA-256
  const signature = await crypto.subtle.sign('HMAC', key, data);

  // Convert the signature (ArrayBuffer) to a hexadecimal string
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export const isNetworkError = (error: any): boolean => {
  if (!error) return false;

  if (error.name === 'TypeError' && error.message.includes('fetch'))
    return true;
  if (error.code === 'ERR_NETWORK') return true;
  if (error.message?.includes('NetworkError')) return true;
  if (error.message?.includes('Failed to fetch')) return true;

  if (!error.response && error.request) return true;

  if (error.message?.includes('Network request failed')) return true;
  if (error.message?.includes('Connection failed')) return true;

  return false;
};

export const createNetworkError = (): ApiError => ({
  type: 'network',
  message:
    'No network connection. Please check your internet connection and try again.',
  retryable: true,
});

export const createApiError = (customMessage?: string): ApiError => ({
  type: 'api',
  message:
    customMessage ||
    'Unable to process your request. Please try again or contact support if the problem persists.',
  retryable: true,
});

export const createValidationError = (message: string): ApiError => ({
  type: 'validation',
  message,
  retryable: false,
});

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
};

// Remove fields that serialize to null-ish values in API payloads.
export const sanitizePayload = (value: unknown): unknown => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === 'number' && Number.isNaN(value)) {
    return undefined;
  }

  if (typeof value === 'string' && value.trim() === '') {
    return undefined;
  }

  if (Array.isArray(value)) {
    const sanitizedArray = value
      .map((item) => sanitizePayload(item))
      .filter((item) => item !== undefined);
    return sanitizedArray;
  }

  if (isPlainObject(value)) {
    const sanitizedObject = Object.entries(value).reduce<
      Record<string, unknown>
    >((acc, [key, fieldValue]) => {
      const sanitizedValue = sanitizePayload(fieldValue);
      if (sanitizedValue !== undefined) {
        acc[key] = sanitizedValue;
      }
      return acc;
    }, {});

    if (Object.keys(sanitizedObject).length === 0) {
      return undefined;
    }

    return sanitizedObject;
  }

  return value;
};
